import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { InterfaceContext } from '../../../contexts/interface-context';
import { ConfigContext } from '../../../contexts/config-context';
import { UserContext, profilePhoto } from '../../../contexts/userContext';
import UserAPI from '../../../axios/user';
import { useCounts } from '../../../contexts/counts-context';
import { PanelIcon } from '../../../pages/user-panel/user-panel';
import { Logout } from '../../logout/logout';
import { BrandLogo } from './brand-logo';
import '../../../hs-theme.css';
import './site-header.css';

const NAV_ITEMS = [
    { title: 'buy', path: '/prodaja' },
    { title: 'rent', path: '/izdavanje' },
    { title: 'daily', path: '/stan-na-dan' },
    { title: 'info', path: '/uputstvo' },
];

const SiteHeader = React.memo(() => {
    const { i18n, t } = useTranslation(["navigation"]);
    const { user, setUser } = useContext(UserContext);
    const { api } = useContext(ConfigContext);
    const {
        responsiveMenuVisible,
        openResponsiveMenu
    } = useContext(InterfaceContext);
    const location = useLocation();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);
    const counts = useCounts();
    const unread = (counts.messages || 0) + (counts.notifications || 0);
    const following = counts.following || 0;

    useEffect(() => {
        if (localStorage.getItem("i18nextLng")?.length > 2) {
            i18n.changeLanguage("sr");
        }
    }, []);

    useEffect(() => {
        const close = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    useEffect(() => {
        setUserMenuOpen(false);
    }, [location.pathname]);

    // the login response has no photo, so it is loaded once and kept with the stored user
    useEffect(() => {
        if (!user || user.photo !== undefined) return;
        UserAPI.getProfileData().then(res => {
            const updated = { ...user, photo: profilePhoto(res.data.photo) };
            localStorage.setItem("user", JSON.stringify(updated));
            setUser(updated);
        }).catch(err => console.log(err));
    }, [user]);

    const lang = i18n.language === 'en' ? 'en' : 'sr';
    const clearPage = () => sessionStorage.removeItem('currentPage');

    return (
        <header className='hs hs-header'>
            <div className='hs-header-inner'>
                <Link to='/' className='hs-brand'>
                    <BrandLogo />
                </Link>
                <nav className='hs-header-nav'>
                    {!responsiveMenuVisible && NAV_ITEMS.map((item) => (
                        <Link key={item.path} to={item.path} onClick={clearPage} className='hs-header-nav-link'>
                            {t(item.title)}
                        </Link>
                    ))}
                </nav>
                <div className='hs-header-actions'>
                    {!responsiveMenuVisible && <>
                        <div className='hs-lang'>
                            <button className={lang === 'sr' ? 'on' : ''} onClick={() => i18n.changeLanguage('sr')}>SR</button>
                            <button className={lang === 'en' ? 'on' : ''} onClick={() => i18n.changeLanguage('en')}>EN</button>
                        </div>
                        {user ? (
                            <div className='hs-user' ref={userMenuRef}>
                                <button className='hs-header-link hs-user-chip' onClick={() => setUserMenuOpen(open => !open)}>
                                    <span className='hs-user-avatar' style={user.photo ? { backgroundImage: `url(${api.host}:${api.port}${user.photo})` } : undefined}>
                                        {!user.photo && `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
                                    </span>
                                    {user.first_name + ' ' + user.last_name}
                                </button>
                                {unread > 0 && <span className='hs-user-badge'>{unread}</span>}
                                {userMenuOpen && (
                                    <ul className='hs-user-menu'>
                                        <li><Link to="/korisnik/podaci">{t('my_panel')}</Link></li>
                                        <Logout />
                                    </ul>
                                )}
                            </div>
                        ) : (
                            <Link to='/login' className='hs-header-link'>{t('login_short')}</Link>
                        )}
                    </>}
                    {user &&
                        <Link to='/postavi-oglas' className='hs-post-btn'>
                            <span className='hs-post-btn-plus'>+</span>{t('create_listing')}
                        </Link>}
                    {responsiveMenuVisible &&
                        <Link to={user ? '/korisnik/praceni' : '/login'} onClick={clearPage} className='hs-header-follow'>
                            <span className='hs-header-follow-icon'>
                                <PanelIcon name='heart' size={22} />
                                {following > 0 && <b>{following}</b>}
                            </span>
                            {t('header_following')}
                        </Link>}
                    {responsiveMenuVisible &&
                        <button className='hs-burger' onClick={openResponsiveMenu} aria-label='Menu'>
                            {unread > 0 && <b className='hs-user-badge'>{unread}</b>}
                            <span></span><span></span><span></span>
                        </button>}
                </div>
            </div>
        </header>
    );
});

export { SiteHeader };
